import { CircleCheck, CircleDashed, Clock, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { Network } from '../../api/types';
import { ActionButton, IconButton } from '../../components/ui';
import { formatDateTime, formatRelative } from '../../lib/format';
import { strings } from '../../strings';
import { useManageNetworks } from '../shared';
import { AttachButton } from './AttachButton';
import { totalAttachments } from './topology';

const p = strings.networks.page;

function Step({
  index,
  done,
  later,
  title,
  children,
}: {
  index: number;
  done: boolean;
  later?: boolean;
  title: string;
  children?: ReactNode;
}) {
  const Icon = later ? Clock : done ? CircleCheck : CircleDashed;
  return (
    <li
      className={`net-step ${done ? 'is-done' : ''} ${later ? 'is-later' : ''}`}
      data-testid={`step-${index}`}
    >
      <Icon aria-hidden className="net-step-icon size-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">
          {title}{' '}
          <span className="text-[12px] font-normal text-fg-muted">
            ({later ? p.comingLater : done ? p.done : p.notDone})
          </span>
        </p>
        {children}
      </div>
    </li>
  );
}

/**
 * Getting started (spec 11 6.15.5, U1.1): plan and apply, attach, apply each attached deployment, later steps. Shown
 * instead of the diagram while the network has no attachments, and from the ⋯ menu later (with Close).
 */
export function GettingStarted({
  network,
  busy,
  onPlan,
  onAttach,
  onClose,
}: {
  network: Network;
  busy: boolean;
  onPlan: () => void;
  onAttach: () => void;
  onClose?: () => void;
}) {
  const manage = useManageNetworks();
  const applied = network.lastSuccessfulApply;
  const attached = totalAttachments(network.attachments);
  const unapplied = network.attachments.pending + network.attachments.waitingForApply;
  return (
    <section className="card net-checklist" aria-labelledby="net-checklist-title">
      <div className="flex items-center justify-between gap-2">
        <h2 id="net-checklist-title" className="text-[15px] font-semibold">
          {p.checklist}
        </h2>
        {onClose && (
          <IconButton label={p.closeChecklist} onClick={onClose}>
            <X aria-hidden className="size-4" />
          </IconButton>
        )}
      </div>
      <ol aria-label={p.checklistLabel(network.name)} className="mt-2 space-y-3">
        <Step index={1} done={!!applied} title={p.step1}>
          {applied ? (
            <p className="text-[13px]">
              <Link
                className="link"
                to={`/runs/${applied.runId}`}
                title={formatDateTime(applied.completedAt)}
              >
                {p.step1Done(formatRelative(applied.completedAt))}
              </Link>
            </p>
          ) : manage ? (
            <ActionButton
              permission="manage-networks"
              className="btn-sm mt-1"
              disabled={busy}
              onClick={onPlan}
            >
              {p.planNetwork}
            </ActionButton>
          ) : (
            <p className="text-[13px] text-fg-muted">{strings.networks.askAdminToApply(network.name)}</p>
          )}
        </Step>
        <Step index={2} done={attached > 0} title={p.step2}>
          <div className="mt-1">
            <AttachButton network={network} onAttach={onAttach} />
          </div>
        </Step>
        <Step index={3} done={attached > 0 && unapplied === 0} title={p.step3}>
          <p className="text-[13px] text-fg-muted">{p.step3Text}</p>
        </Step>
        <Step index={4} done={false} later title={p.step4} />
      </ol>
    </section>
  );
}
